import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAlbums, fetchAllData } from '../lib/api';
import type { Album } from '../types/models';

export function Albums() {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [artistNames, setArtistNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        // Fetch all albums
        const albumsData = await fetchAlbums();
        setAlbums(albumsData);
        
        // Get artist data to display artist names
        const appData = await fetchAllData();
        
        // Create a map of artistId -> artist name
        const artistMap: Record<string, string> = {};
        appData.artists.forEach(artist => {
          artistMap[artist.id] = artist.name;
        });
        
        setArtistNames(artistMap);
      } catch (error) {
        console.error('Error loading albums:', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-naia-pink text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <h1 className="font-outfit text-4xl font-bold text-pearl-white mb-8 relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
        Albums
      </h1>
      
      {albums.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
          {albums.map(album => (
            <Link 
              key={album.id} 
              to={`/album/${album.id}`} 
              className="group"
            >
              <div className="aspect-square rounded-xl overflow-hidden relative mb-3 ring-1 ring-white/10 group-hover:ring-naia-pink/50 transition-all duration-300 shadow-xl group-hover:shadow-naia-pink/20">
                <img 
                  src={album.coverUrl} 
                  alt={album.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep-night/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <span className="text-pearl-white font-medium truncate">View Album</span>
                </div>
              </div>
              
              <h3 className="font-medium text-pearl-white truncate group-hover:text-naia-pink transition-colors duration-300">
                {album.title}
              </h3>
              
              <p className="text-sm text-slate-grey truncate">
                {artistNames[album.artistId] || 'Unknown Artist'}
              </p>
              
              <p className="text-xs text-slate-grey/70 mt-1">
                {new Date(album.releaseDate).getFullYear()}
              </p>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-slate-grey text-lg mb-4">No albums available yet</p>
          <p className="text-pearl-white text-sm">Check back soon for album releases!</p>
        </div>
      )}
    </div>
  );
}
